import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { TrendAnalysisFilterHistoryStateModel } from 'src/core/models/analysis/trend-analysis/trend-analysis-filter-history.state-model';
import { UserStateSettingsService } from 'src/core/services/state/user-state-settings.service';
import { GenericState } from '../generic/generic.state';
import {
  FilterHistoryAdd,
  FilterHistoryBack,
  FilterHistoryForward,
  FilterHistoryReset,
  FilterHistoryClear,
} from 'src/core/actions/analysis/trend-analysis-filter-history.actions';
import { ObjectHelper } from 'src/core/helpers/object.helper';
import { TrendHistoryItem } from 'src/core/models/analysis/trend-analysis/trend-history-item-model';

const name = 'TrendAnalysisFilterHistoryState';

@State<TrendAnalysisFilterHistoryStateModel>({
  name: name,
  defaults: {
    filterHistory: [],
    currentHistoryIndex: -1,
    isFiltering: false,
  } as TrendAnalysisFilterHistoryStateModel,
})
@Injectable()
export class TrendAnalysisFilterHistoryState extends GenericState<TrendAnalysisFilterHistoryState> {
  @Selector()
  static getFilterHistory(state: TrendAnalysisFilterHistoryStateModel): any[] {
    return state.filterHistory;
  }

  @Selector()
  static getCurrentHistoryIndex(state: TrendAnalysisFilterHistoryStateModel): number {
    return state.currentHistoryIndex;
  }

  @Selector()
  static isFiltering(state: TrendAnalysisFilterHistoryStateModel): boolean {
    return state.isFiltering;
  }

  @Selector()
  static canGoBack(state: TrendAnalysisFilterHistoryStateModel): boolean {
    const currentItem =
      state.currentHistoryIndex >= 0 ? state.filterHistory[state.currentHistoryIndex] : null;

    if (!state.isFiltering || state.currentHistoryIndex <= 0) {
      return false;
    }

    return !currentItem?.isBaseFilter;
  }

  @Selector()
  static canGoForward(state: TrendAnalysisFilterHistoryStateModel): boolean {
    return state.currentHistoryIndex < state.filterHistory.length - 1 && state.isFiltering;
  }

  @Selector()
  static canReset(state: TrendAnalysisFilterHistoryStateModel): boolean {
    if (!state.isFiltering || state.currentHistoryIndex < 0) {
      return false;
    }

    const currentItem = state.filterHistory[state.currentHistoryIndex];

    if (currentItem?.isBaseFilter) {
      return false;
    }

    return state.filterHistory.some(x => x?.isBaseFilter);
  }

  @Selector()
  static getCurrentFilter(state: TrendAnalysisFilterHistoryStateModel): any {
    if (state.filterHistory.length === 0 || state.currentHistoryIndex < 0) {
      return null;
    }
    return state.filterHistory[state.currentHistoryIndex];
  }

  @Action(FilterHistoryAdd)
  addFilter(ctx: StateContext<TrendAnalysisFilterHistoryStateModel>, action: FilterHistoryAdd) {
    const currentState = ctx.getState();
    const deepCopyFilter: TrendHistoryItem = ObjectHelper.deepCopy(action.payload);

    // Remove any future history if we're not at the end
    if (currentState.currentHistoryIndex < currentState.filterHistory.length - 1) {
      currentState.filterHistory = currentState.filterHistory.slice(
        0,
        currentState.currentHistoryIndex + 1,
      );
    }

    // Add the new filter to history
    currentState.filterHistory.push(deepCopyFilter);
    currentState.currentHistoryIndex = currentState.filterHistory.length - 1;
    currentState.isFiltering = true;

    this.userSettingsService.patchState(ctx, currentState, name);
  }

  @Action(FilterHistoryBack)
  goBack(ctx: StateContext<TrendAnalysisFilterHistoryStateModel>) {
    const currentState = ctx.getState();

    if (currentState.currentHistoryIndex > 0) {
      currentState.currentHistoryIndex--;
      this.userSettingsService.patchState(ctx, currentState, name);
    }
  }

  @Action(FilterHistoryForward)
  goForward(ctx: StateContext<TrendAnalysisFilterHistoryStateModel>) {
    const currentState = ctx.getState();

    if (currentState.currentHistoryIndex < currentState.filterHistory.length - 1) {
      currentState.currentHistoryIndex++;
      this.userSettingsService.patchState(ctx, currentState, name);
    }
  }

  @Action(FilterHistoryReset)
  reset(ctx: StateContext<TrendAnalysisFilterHistoryStateModel>) {
    const currentState = ctx.getState();

    const lastBaseIndex = [...currentState.filterHistory]
      .map((item, index) => ({ item, index }))
      .reverse()
      .find(x => x.item?.isBaseFilter)?.index;

    if (lastBaseIndex != null) {
      currentState.currentHistoryIndex = lastBaseIndex;
      this.userSettingsService.patchState(ctx, currentState, name);
    }
  }

  @Action(FilterHistoryClear)
  clear(ctx: StateContext<TrendAnalysisFilterHistoryStateModel>) {
    const newState: TrendAnalysisFilterHistoryStateModel = {
      filterHistory: [],
      currentHistoryIndex: -1,
      isFiltering: false,
    };

    ctx.setState(newState);
    this.userSettingsService.patchState(ctx, newState, name);
  }

  constructor(userSettingsService: UserStateSettingsService) {
    super(userSettingsService);
    this.name = name;
    this.userSettingsService.register(this.name, this);
  }
}
